import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Search, ArrowRight, X, Users, School, FileText, DollarSign,
  BarChart3, Calendar, Inbox, GraduationCap, Activity, Sparkles,
  Plus, ClipboardList,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * MESP Command Palette — Phase 9 Wave 3 (Feb 2026, UX overhaul).
 *
 * A global "jump to anything" interface bound to Cmd/Ctrl+K. Combines:
 *   • Static navigation actions (every Hub page + Quick Create actions)
 *   • Live student search (via /api/admin/students/pipeline?search=…)
 *   • Recent items (last 5 visited routes from localStorage)
 *
 * Keyboard:
 *   • Cmd/Ctrl+K toggles open
 *   • Esc closes
 *   • ↑ / ↓ navigate
 *   • Enter selects (navigates / runs action)
 *
 * The palette is permission-aware: actions hidden for non-admin roles.
 */
export const CommandPalette = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [studentMatches, setStudentMatches] = useState([]);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef(null);
  const itemsRef = useRef([]);

  // PBAC helper — Super_Admin always passes; others need permission key.
  const can = (key) => {
    if (!user) return false;
    if (user.role === 'Super_Admin') return true;
    if (user.permission_profile && user.permission_profile[key] === true) return true;
    if (Array.isArray(user.permissions) && user.permissions.includes(key)) return true;
    return false;
  };

  const isAdmin = can('can_access_students') || can('can_manage_users') || can('can_access_finance');

  // ── Hotkey: Cmd/Ctrl+K toggles open ──────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (open && e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // ── When opened: clear query, focus input, reset cursor ─────
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
      setStudentMatches([]);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // ── Live student search (debounced, admin-only) ──────────────
  useEffect(() => {
    if (!open || !isAdmin || query.trim().length < 2) {
      setStudentMatches([]);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const id = setTimeout(async () => {
      try {
        const r = await axios.get(
          `${API_URL}/api/admin/students/pipeline?search=${encodeURIComponent(query.trim())}&limit_per_stage=5`,
          { withCredentials: true },
        );
        if (cancelled) return;
        const flat = (r.data?.stages || []).flatMap((s) => s.students || []);
        setStudentMatches(flat.slice(0, 6));
      } catch {
        if (!cancelled) setStudentMatches([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(id); };
  }, [query, open, isAdmin]);

  // Navigation & Quick Create actions — permission-gated (Feb 2026, PBAC).
  const navActions = useMemo(() => {
    const all = [
      { id: 'nav-dashboard',     icon: BarChart3,    label: 'لوحة التحكم',          keywords: 'dashboard لوحة',        path: '/dashboard',      perm: null },
      { id: 'nav-inbox',         icon: Inbox,        label: 'الوارد والطلبات',       keywords: 'inbox tasks وارد طلبات', path: '/inbox',          perm: 'can_manage_users' },
      { id: 'nav-pipeline',      icon: Sparkles,     label: 'خط أنابيب الطلاب (Pipeline)', keywords: 'pipeline kanban خط أنابيب توزيع', path: '/students?view=pipeline', perm: 'can_access_students' },
      { id: 'nav-students',      icon: Users,        label: 'الطلاب',               keywords: 'students طلاب',         path: '/students',       perm: 'can_access_students' },
      { id: 'nav-academic',      icon: GraduationCap, label: 'القاعات والتوزيع',     keywords: 'classrooms قاعات توزيع distribution', path: '/academic-hub',   perm: 'can_access_classrooms' },
      { id: 'nav-operations',    icon: Activity,     label: 'العمليات اليومية',     keywords: 'operations daily attendance حضور', path: '/operations-hub', perm: 'can_access_students' },
      { id: 'nav-finance',       icon: DollarSign,   label: 'المالية',              keywords: 'finance payments مالية مدفوعات', path: '/finance-hub',    perm: 'can_access_finance' },
      { id: 'nav-reports',       icon: BarChart3,    label: 'التقارير والتحليلات',  keywords: 'reports analytics تقارير', path: '/reports-hub',    perm: 'can_access_reports' },

      // Quick Create
      { id: 'create-classroom',  icon: Plus,         label: 'إنشاء قاعة جديدة',     keywords: 'new classroom قاعة',     path: '/classrooms?action=create', perm: 'can_access_classrooms', badge: 'إنشاء' },
      { id: 'create-assignment', icon: ClipboardList, label: 'إنشاء واجب',         keywords: 'new assignment واجب',    path: '/assignments?action=create', perm: 'can_access_assignments', badge: 'إنشاء' },
      { id: 'view-trials',       icon: Calendar,     label: 'متابعة التجارب',       keywords: 'trials تجارب trial',     path: '/trials',                perm: 'can_access_trials' },
      // Audit log remains Super_Admin-only — there is no granular
      // permission key for it. Keep the role gate.
      { id: 'view-audit',        icon: FileText,     label: 'سجل الأحداث (Audit Log)', keywords: 'audit logs سجل',      path: '/audit-logs',            roleOnly: 'Super_Admin' },
    ];
    const visible = all.filter((a) => {
      if (a.roleOnly) return user?.role === a.roleOnly;
      if (!a.perm) return true;
      return can(a.perm);
    });
    if (!query.trim()) return visible;
    const q = query.toLowerCase().trim();
    return visible.filter((a) =>
      a.label.toLowerCase().includes(q) || (a.keywords || '').toLowerCase().includes(q),
    );
  }, [query, user]);

  // Combine all items in order for keyboard cursor.
  const flatItems = useMemo(() => {
    const items = [];
    navActions.forEach((a) => items.push({ type: 'nav', data: a }));
    studentMatches.forEach((s) => items.push({ type: 'student', data: s }));
    return items;
  }, [navActions, studentMatches]);

  useEffect(() => { setActiveIndex(0); }, [flatItems.length]);

  // Phase 9 Wave 3 — keep refs in sync so the keydown handler always
  // reads the CURRENT flatItems / activeIndex. Without this the
  // closure inside `handleKeyDown` may snapshot a stale flatItems[]
  // (e.g. right after a filter changes and the user immediately hits
  // Enter), which previously stripped the query string from paths
  // like `/students?view=pipeline`.
  const flatItemsRef = useRef(flatItems);
  const activeIndexRef = useRef(activeIndex);
  useEffect(() => { flatItemsRef.current = flatItems; }, [flatItems]);
  useEffect(() => { activeIndexRef.current = activeIndex; }, [activeIndex]);

  // ── Keyboard nav inside the palette ─────────────────────────
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, (flatItemsRef.current.length || 1) - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // Read the LATEST items + index via refs to avoid stale closures.
      const items = flatItemsRef.current;
      const idx = Math.min(activeIndexRef.current, items.length - 1);
      const item = items[idx];
      if (item) selectItem(item);
    }
  };

  const selectItem = (item) => {
    setOpen(false);
    if (item.type === 'nav') {
      navigate(item.data.path);
    } else if (item.type === 'student') {
      navigate(`/students?view=pipeline&student=${item.data.student_profile_id}`);
    }
  };

  // Auto-scroll active item into view.
  useEffect(() => {
    const el = itemsRef.current[activeIndex];
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-start justify-center pt-[10vh] px-4"
      style={{ direction: 'rtl' }}
      data-testid="cmd-palette-root"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-[#0A1128]/70 backdrop-blur-sm"
        onClick={() => setOpen(false)}
        data-testid="cmd-palette-backdrop"
      />
      {/* Panel */}
      <div
        className="relative w-full max-w-2xl bg-white border border-[#E2E4E0] rounded-lg shadow-2xl overflow-hidden"
        data-testid="cmd-palette-panel"
        onKeyDown={handleKeyDown}
      >
        {/* Input row */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E2E4E0]">
          <Search size={18} className="text-[#5C6B8C] shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="ابحث عن صفحة، طالب، أو إجراء..."
            className="flex-1 bg-transparent outline-none text-sm text-[#0A1128] placeholder-[#5C6B8C]"
            data-testid="cmd-palette-input"
          />
          <kbd className="hidden sm:inline-flex items-center px-2 py-0.5 rounded border border-[#E2E4E0] bg-[#F5F5F0] text-[10px] text-[#5C6B8C] tabular-nums" dir="ltr">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto py-1" data-testid="cmd-palette-results">
          {/* Navigation group */}
          {navActions.length > 0 && (
            <Section title="التنقل والإجراءات">
              {navActions.map((a, idx) => {
                const globalIdx = idx;
                return (
                  <Item
                    key={a.id}
                    ref={(el) => (itemsRef.current[globalIdx] = el)}
                    isActive={activeIndex === globalIdx}
                    onClick={() => selectItem({ type: 'nav', data: a })}
                    onMouseEnter={() => setActiveIndex(globalIdx)}
                    icon={a.icon}
                    label={a.label}
                    badge={a.badge}
                    testId={`cmd-palette-item-${a.id}`}
                  />
                );
              })}
            </Section>
          )}

          {/* Student matches group */}
          {isAdmin && (
            <>
              {searching && (
                <p className="px-4 py-2 text-[11px] text-[#5C6B8C]" data-testid="cmd-palette-searching">
                  جاري البحث عن طلاب...
                </p>
              )}
              {studentMatches.length > 0 && (
                <Section title={`الطلاب (${studentMatches.length})`}>
                  {studentMatches.map((s, idx) => {
                    const globalIdx = navActions.length + idx;
                    return (
                      <Item
                        key={s.student_profile_id}
                        ref={(el) => (itemsRef.current[globalIdx] = el)}
                        isActive={activeIndex === globalIdx}
                        onClick={() => selectItem({ type: 'student', data: s })}
                        onMouseEnter={() => setActiveIndex(globalIdx)}
                        icon={Users}
                        label={s.full_name || '—'}
                        meta={[s.current_status, s.current_classroom, s.phone_number].filter(Boolean).join(' • ')}
                        testId={`cmd-palette-item-student-${s.student_profile_id}`}
                      />
                    );
                  })}
                </Section>
              )}
              {!searching && query.trim().length >= 2 && studentMatches.length === 0 && (
                <p className="px-4 py-2 text-[11px] text-[#5C6B8C]">لا توجد نتائج طلاب لـ &quot;{query}&quot;.</p>
              )}
            </>
          )}

          {flatItems.length === 0 && !searching && (
            <div className="px-4 py-8 text-center text-sm text-[#5C6B8C]" data-testid="cmd-palette-no-results">
              لا توجد نتائج مطابقة.
            </div>
          )}
        </div>

        {/* Footer hints */}
        <div className="px-4 py-2 border-t border-[#E2E4E0] bg-[#F8F8F4] flex items-center justify-between text-[10px] text-[#5C6B8C]">
          <span className="inline-flex items-center gap-3">
            <Kbd>↑</Kbd> <Kbd>↓</Kbd> للتنقل
            <Kbd>Enter</Kbd> للاختيار
          </span>
          <span>افتح بـ <Kbd>⌘</Kbd> <Kbd>K</Kbd></span>
        </div>
      </div>
    </div>
  );
};

// ── Sub-components ────────────────────────────────────────────
const Section = ({ title, children }) => (
  <div className="py-1.5">
    <p className="px-4 py-1 text-[10px] uppercase tracking-wider text-[#5C6B8C] font-semibold">
      {title}
    </p>
    <div>{children}</div>
  </div>
);

const Item = React.forwardRef(({ isActive, icon: Icon, label, meta, badge, onClick, onMouseEnter, testId }, ref) => (
  <button
    ref={ref}
    type="button"
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    className={`w-full flex items-center gap-3 px-4 py-2.5 text-start transition ${
      isActive ? 'bg-[#0A1128] text-[#C9A24E]' : 'bg-white text-[#0A1128] hover:bg-[#F5F5F0]'
    }`}
    data-testid={testId}
    aria-selected={isActive}
  >
    <Icon size={15} className={isActive ? 'text-[#C9A24E]' : 'text-[#5C6B8C]'} />
    <span className="flex-1 min-w-0">
      <span className="block text-sm font-medium truncate">{label}</span>
      {meta && <span className={`block text-[11px] truncate ${isActive ? 'text-white/70' : 'text-[#5C6B8C]'}`}>{meta}</span>}
    </span>
    {badge && (
      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
        isActive ? 'bg-[#C9A24E] text-[#0A1128]' : 'bg-[#0A1128] text-white'
      }`}>{badge}</span>
    )}
    <ArrowRight size={13} className={isActive ? 'text-[#C9A24E]' : 'text-[#5C6B8C]/40'} />
  </button>
));

const Kbd = ({ children }) => (
  <kbd className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded border border-[#E2E4E0] bg-white text-[10px] text-[#0A1128] tabular-nums" dir="ltr">
    {children}
  </kbd>
);

export default CommandPalette;
